import type { AttributeKey, Language } from '@ps/poker-games-tile-mappers';
import { utils } from '@ps/poker-games-tile-mappers';
import * as Sentry from '@sentry/react';

interface Payload {
  id?: number;
  fee: number;
  buyIn: number;
  currency: string;
  whenStart: string;
  maxPlayers: number;
  minPlayers: number;
  legacyStatus: number;
  prizePool: number;
  prizePoolValue: number;
  prizePoolUnit: number;
  winnerAward?: number;
  whenStartInt: number;
  regEndInt: number;
}

type AttributeKeyMap<T> = Partial<{
  [K in AttributeKey]: keyof T;
}>;

type Result<A> = {
  [K in keyof A]: string;
};

export const formatTournamentAttr = <T extends Payload, A>(
  tournament: T,
  locale: string,
  isPlayMoney: boolean,
  timeZone: string,
  attributeKeyMap: AttributeKeyMap<A>
): Result<A> => {
  const formattedAttr = Object.values(attributeKeyMap).reduce((acc, key) => {
    acc[key] = 'N/A';
    return acc;
  }, {} as Result<A>);

  try {
    const attributes = utils.mttUtils.formatMttAttr(
      {
        isPlayMoney: Number(isPlayMoney),
        buyIn: tournament.buyIn,
        rake: tournament.fee,
        maxPlayers: tournament.maxPlayers,
        minPlayers: tournament.minPlayers,
        fakeStatus: tournament.legacyStatus,
        whenStart: tournament.whenStart,
        whenStartInt: tournament.whenStartInt,
        regEndAbsInt: tournament.regEndInt,
        prizePoolToPublish: tournament.prizePool,
        prizePoolUnit: tournament.prizePoolUnit,
        currency: tournament.currency,
        guaranteedPrizePool: tournament.prizePoolValue,
        scalePM: 0,
        winnerAward: tournament?.winnerAward
      },
      {
        language: locale as Language,
        disabledGameTypes: [],
        hspFlagsMap: '',
        isPPPRevampNeymar: false,
        timeZone
      }
    );

    attributes?.forEach((attr) => {
      const key = attributeKeyMap[attr.key] || '';

      if (key) formattedAttr[key] = attr.value;
    });
  } catch (err) {
    Sentry.captureException(err, {
      extra: {
        timeZone,
        locale,
        whenStartInt: tournament.whenStartInt,
        whenStart: tournament.whenStart,
        fakeStatus: tournament.legacyStatus,
        regEndAbsInt: tournament.regEndInt,
        id: tournament.id
      }
    });
  }

  return formattedAttr;
};
